/**
 * Template-type → Compliance-category mapping. (FEAT-0011.)
 *
 * The Create Template page stores `templateType` as the DB row id from
 * `tbl_message_type` (seeded by migration
 * `20260514120000-seed-tbl-message-type.js`). The Compliance Reviewer
 * agent expects the canonical UPPER-CASE category name. This map is
 * the single source of truth for that translation; if the migration
 * changes ids both sides update here.
 *
 * Mapping rationale:
 *   1 — Authentication / OTP → AUTHENTICATION (WhatsApp category)
 *   2 — Transactional       → UTILITY        (WhatsApp category)
 *   3 — Marketing           → MARKETING      (WhatsApp category)
 *   4 — Informational       → INFORMATIONAL  (Meta has no separate WA
 *                                              bucket — agent maps to
 *                                              UTILITY-like rules but
 *                                              keeps the label so non-WA
 *                                              channels can apply the
 *                                              informational rules.)
 */
export const TEMPLATE_TYPE_TO_CATEGORY = Object.freeze({
  "1": "AUTHENTICATION",
  "2": "UTILITY",
  "3": "MARKETING",
  "4": "INFORMATIONAL",
});

export const CATEGORY_LABELS = Object.freeze({
  AUTHENTICATION: "Authentication / OTP",
  UTILITY:        "Transactional",
  MARKETING:      "Marketing",
  INFORMATIONAL:  "Informational",
});

/**
 * Resolve a compliance category from either:
 *   - a templateType id ("1" .. "4")
 *   - the named-template-type row from tbl_message_type (object with
 *     id + name) — useful when the operator added custom rows
 *
 * Returns null when no match; callers should still let the operator
 * override in the modal.
 */
export function resolveComplianceCategory(templateType, templateTypeRows = []) {
  if (templateType == null || templateType === "") return null;
  const idStr = String(templateType);
  const direct = TEMPLATE_TYPE_TO_CATEGORY[idStr];
  if (direct) return direct;
  // Fallback: match on the row name (operator-added custom row).
  const row = templateTypeRows.find((t) => String(t.id) === idStr);
  if (!row) return null;
  const n = (row.name || "").toLowerCase();
  if (n.includes("auth") || n.includes("otp"))           return "AUTHENTICATION";
  if (n.includes("transact") || n.includes("utility"))   return "UTILITY";
  if (n.includes("market"))                              return "MARKETING";
  if (n.includes("inform"))                              return "INFORMATIONAL";
  return null;
}

/**
 * Reverse: when the modal is opened standalone (no template type set
 * yet) and the operator picks a category manually, surface a
 * friendly label.
 */
export function labelForCategory(category) {
  return CATEGORY_LABELS[category] || category || "—";
}
